import { makeAutoObservable, runInAction } from "mobx";
import { showToast } from "@/lib/utils/toast";
import { t } from "i18next";
import { authStore } from "@/features/auth/stores";
import { esgService } from "../services";
import {
  getDisclosureIdentifier,
  normalizeDisclosure,
} from "../utils/disclosure-utils";
import {
  extractFrameworks,
  extractDisclosures,
  extractTargets,
  extractDeletedCount,
  extractDataPayload,
  normalizeFramework,
} from "../utils/api-normalizers";
import { storage } from "../utils/storage";
import { ESG_FRAMEWORKS } from "@/lib/constants/frameworks.constants";

const STORAGE_KEYS = {
  framework: "nxstart_kpi_framework",
  year: "nxstart_kpi_year",
  category: "nxstart_kpi_category",
};

const DEFAULT_FRAMEWORKS = [...ESG_FRAMEWORKS];
const createFilters = () => ({
  framework: "All",
  year: "All",
  organization: "All",
});
const DEFAULT_PAGE_SIZE = 10;
const DEFAULT_SORT = { key: "created_at", direction: "desc" };

/**
 * KPI Targets Store
 * Manages quantitative disclosure targets and frameworks
 */
class KpiTargetsStore {
  // State
  targets = new Map();
  changedTargets = new Set();
  disclosures = new Map();
  selectedMetricIds = new Set();
  frameworks = [];
  filters = createFilters();

  // Loading states
  loadingFrameworks = false;
  loadingDisclosures = false;
  loadingTargets = false;
  savingTargets = false;
  seedingTargets = false;
  resettingTargets = false;
  deleteLoading = false;

  // Filter state
  selectedFramework = null;
  selectedYear = new Date().getFullYear();
  activeCategory = "Environmental";
  targetSearchTerm = "";
  page = 1;
  pageSize = DEFAULT_PAGE_SIZE;
  sort = DEFAULT_SORT;
  yearLists = [];
  frameworkLists = [];
  targetSettingsList = [];
  targetSettingListData = { isallOrganization: true };
  selectedOrganization = null;
  filterTargetList = [];
  // Organization context
  orgId = null;
  isQuarterly = false;
  selectedTarget = null;

  constructor() {
    makeAutoObservable(this, {}, { autoBind: false });
    this.hydrateSelections();
  }

  /**
   * Restore filter selections from localStorage
   */
  hydrateSelections = () => {
    const storedFramework = storage.read(STORAGE_KEYS.framework);
    const storedYear = storage.read(STORAGE_KEYS.year);
    const storedCategory = storage.read(STORAGE_KEYS.category);

    if (storedFramework) {
      this.selectedFramework = storedFramework;
    }
    if (storedYear && !Number.isNaN(Number(storedYear))) {
      this.selectedYear = Number(storedYear);
    }
    if (storedCategory) {
      this.activeCategory = storedCategory;
    }
  };

  // Computed properties
  get isEnvOnly() {
    return authStore.user?.accountType === "esg_env_only";
  }

  get hasDisclosuresLoaded() {
    return this.disclosures.size > 0;
  }

  get allTargets() {
    return Array.from(this.targets.values());
  }

  get hasChangedTargets() {
    return this.changedTargets.size > 0;
  }

  get selectedMetricIdsList() {
    return Array.from(this.selectedMetricIds.values());
  }

  get hasSelectedMetrics() {
    return this.selectedMetricIds.size > 0;
  }

  // Filter setters
  setSelectedFramework = (framework) => {
    this.selectedFramework = framework || "All";
    storage.write(STORAGE_KEYS.framework, this.selectedFramework);
  };

  setFilters = (patch) => {
    this.filters = { ...this.filters, ...patch };
    this.page = 1;
    this.filterTargetList = this.targetSettingsList.filter((item) => {
      if (
        this.filters.framework !== "All" &&
        item.framework !== this.filters.framework
      ) {
        return false;
      }
      if (
        this.filters.year !== "All" &&
        item.year !== Number(this.filters.year)
      ) {
        return false;
      }

      if (
        this.filters.organization !== "All" &&
        !item.organization?.includes(this.filters.organization)
      ) {
        return false;
      }
      return true;
    });
  };

  resetFilters = () => {
    this.filters = createFilters();
    this.page = 1;
  };

  setPage = (page) => {
    this.page = page;
  };

  setPageSize = (size) => {
    this.pageSize = size;
  };

  buildParams = ({ companyId }) => {
    const params = {
      companyId,
      page: this.page,
      limit: this.pageSize,
      sortField: this.sort.key,
      sortOrder: this.sort.direction,
    };

    if (this.filters.framework && this.filters.framework !== "All") {
      params.framework = this.filters.framework;
    }

    if (this.filters.year && this.filters.year !== "All") {
      params.year = this.filters.year;
    }

    if (this.filters.organization && this.filters.organization !== "All") {
      params.organization = this.filters.organization;
    }

    return params;
  };

  loadTargetSettingsList = async () => {
    await this.getTargetList();
  };

  setSelectedYear = (year) => {
    const next = Number(year);
    if (!Number.isFinite(next)) return;
    this.selectedYear = next;
    storage.write(STORAGE_KEYS.year, next);
    this.changedTargets.add("year");
  };

  setActiveCategory = (category) => {
    this.activeCategory = category || "All";
    storage.write(STORAGE_KEYS.category, this.activeCategory);
  };

  setTargetSearchTerm = (term) => {
    this.targetSearchTerm = term || "";
  };

  setOrg = (orgId) => {
    this.orgId = orgId || null;
  };

  getMetricIdentifier = (metric) => {
    if (!metric) return null;
    if (typeof metric === "string") {
      const normalized = metric.trim();
      return normalized ? normalized.toUpperCase() : null;
    }
    const disclosureId = getDisclosureIdentifier(metric);
    return disclosureId ? String(disclosureId).toUpperCase() : null;
  };

  setSelectedMetricIds = (metricIds = [], { markChanged = true } = {}) => {
    const normalized = metricIds
      .map((id) => this.getMetricIdentifier(id))
      .filter(Boolean);
    this.selectedMetricIds = new Set(normalized);
    if (markChanged) {
      this.changedTargets.add("selectedMetricIds");
    }
  };

  toggleSelectedMetric = (metric) => {
    const metricId = this.getMetricIdentifier(metric);
    if (!metricId) return;
    if (this.selectedMetricIds.has(metricId)) {
      this.selectedMetricIds.delete(metricId);
    } else {
      this.selectedMetricIds.add(metricId);
    }
    this.changedTargets.add("selectedMetricIds");
  };

  isMetricSelected = (metric) => {
    const metricId = this.getMetricIdentifier(metric);
    return metricId ? this.selectedMetricIds.has(metricId) : false;
  };

  selectMetricIds = (metricIds = []) => {
    const normalized = metricIds
      .map((id) => this.getMetricIdentifier(id))
      .filter(Boolean);
    normalized.forEach((id) => this.selectedMetricIds.add(id));
    this.changedTargets.add("selectedMetricIds");
  };

  unselectMetricIds = (metricIds = []) => {
    const normalized = metricIds
      .map((id) => this.getMetricIdentifier(id))
      .filter(Boolean);
    normalized.forEach((id) => this.selectedMetricIds.delete(id));
    this.changedTargets.add("selectedMetricIds");
  };

  clearSelectedMetricIds = ({ markChanged = true } = {}) => {
    this.selectedMetricIds = new Set();
    if (markChanged) {
      this.changedTargets.add("selectedMetricIds");
    }
  };

  // Target key management
  getTargetKey = (disclosureId, year = null) => {
    const targetYear = year || this.selectedYear;
    return `${disclosureId}_${targetYear}`;
  };

  getTarget = (disclosureId) => {
    const key = this.getTargetKey(disclosureId);
    return this.targets.get(key);
  };

  /**
   * Create default target structure for a disclosure
   */
  getDefaultTarget = (disclosure) => {
    const disclosureId = getDisclosureIdentifier(disclosure);
    const target = {
      disclosureCode: disclosureId,
      disclosureTitle: disclosure.title,
      framework: disclosure.framework,
      category: disclosure.category,
      unit: disclosure.unit,
      annual: 0,
      quarterly: null,
      monthly: null,
      year: this.selectedYear,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };

    return target;
  };

  /**
   * Set annual target value and distribute to periods
   */
  setAnnualTarget = (disclosure, value) => {
    const disclosureId = getDisclosureIdentifier(disclosure);
    const targetKey = this.getTargetKey(disclosureId);
    const annualValue = parseFloat(value) || 0;
    const target =
      this.getTarget(disclosureId) || this.getDefaultTarget(disclosure);

    // Distribute annual value to periods
    const quarterlyValue = annualValue / 4;
    target.quarterly = [
      quarterlyValue,
      quarterlyValue,
      quarterlyValue,
      quarterlyValue,
    ];

    target.annual = annualValue;
    target.year = this.selectedYear;
    target.updatedAt = new Date().toISOString();

    this.targets.set(targetKey, target);
    this.changedTargets.add(targetKey);
  };

  /**
   * Set target value for a specific period and recalculate annual
   */
  setPeriodTarget = (disclosure, periodIndex, value) => {
    const disclosureId = getDisclosureIdentifier(disclosure);
    const targetKey = this.getTargetKey(disclosureId);
    const target =
      this.targets.get(targetKey) || this.getDefaultTarget(disclosure);
    const periodValue = parseFloat(value) || 0;

    target.quarterly[periodIndex] = periodValue;
    target.annual = target.quarterly.reduce((sum, val) => sum + val, 0);

    target.updatedAt = new Date().toISOString();
    this.targets.set(targetKey, target);
    this.changedTargets.add(targetKey);
  };

  /**
   * Clear a single target
   */
  clearTarget = async (disclosureId) => {
    const targetKey = this.getTargetKey(disclosureId);
    const existing = this.targets.get(targetKey);
    if (!existing) return;

    this.targets.delete(targetKey);
    this.changedTargets.delete(targetKey);

    const data = await esgService.deleteQuantitativeTargets({
      year: this.selectedYear,
      masterMetricId: disclosureId,
    });

    if (!data) {
      // Restore on failure
      this.targets.set(targetKey, existing);
      showToast.error(t("Failed to delete target."));
    }
  };

  /**
   * Clear all targets matching current filters
   */
  clearAllTargets = async () => {
    runInAction(() => {
      this.resettingTargets = true;
    });

    const params = { year: this.selectedYear };
    if (this.selectedFramework !== "All") {
      params.framework = this.selectedFramework;
    }
    if (this.activeCategory !== "All") {
      params.category = this.activeCategory;
    }

    try {
      const data = await esgService.deleteQuantitativeTargets(params);

      if (!data) {
        throw new Error("Failed to delete targets.");
      }

      // Remove matching targets from local state
      const keysToDelete = [];
      this.targets.forEach((target, key) => {
        if (target.year !== this.selectedYear) return;
        if (params.framework && target.framework !== params.framework) return;
        if (params.category && target.category !== params.category) return;
        keysToDelete.push(key);
      });

      keysToDelete.forEach((key) => this.targets.delete(key));
      this.changedTargets.clear();

      const deletedCount = extractDeletedCount(data);
      showToast.success(
        t("Deleted {{count}} targets.", { count: deletedCount }),
      );

      return true;
    } catch (error) {
      showToast.error(error?.message || t("Failed to delete targets."));
      return false;
    } finally {
      runInAction(() => {
        this.resettingTargets = false;
      });
    }
  };

  /**
   * Load frameworks from API
   */
  loadFrameworks = async () => {
    if (this.loadingFrameworks || this.frameworks.length > 0) return;

    runInAction(() => {
      this.loadingFrameworks = true;
    });

    try {
      const data = await esgService.getFrameworks({ throwOnError: false });

      if (!data) {
        throw new Error("Failed to load frameworks.");
      }

      const list = extractFrameworks(data);

      runInAction(() => {
        if (Array.isArray(list) && list.length > 0) {
          this.frameworks = list.map(normalizeFramework).filter(Boolean);
        } else {
          this.frameworks = DEFAULT_FRAMEWORKS.map((code) => ({
            code,
            name: code,
          }));
        }
      });
    } catch (error) {
      runInAction(() => {
        this.frameworks = DEFAULT_FRAMEWORKS.map((code) => ({
          code,
          name: code,
        }));
      });
      showToast.error(error?.message || t("Failed to load frameworks."));
    } finally {
      runInAction(() => {
        this.loadingFrameworks = false;
      });
    }
  };

  /**
   * Load disclosures from API
   */
  loadDisclosuresFromApi = async (framework = "All", category = "All") => {
    if (this.loadingDisclosures) return;

    // Return early if already loaded
    if (framework === "All" && this.disclosures.size > 0) {
      return this.getAllQuantitativeDisclosures().length;
    }
    if (framework !== "All" && this.disclosures.has(framework)) {
      return this.disclosures.get(framework)?.length || 0;
    }

    runInAction(() => {
      this.loadingDisclosures = true;
    });

    try {
      const resolvedCategory = this.isEnvOnly ? "Environmental" : category;
      const data = await esgService.getDisclosures({
        framework,
        category: resolvedCategory,
        page: 1,
        limit: 10000,
        throwOnError: false,
      });

      if (!data) {
        throw new Error("Failed to load disclosures.");
      }

      const payload = extractDisclosures(data);

      const seenCodes = new Set();
      const disclosuresList =
        payload.filter((item) => {
          if (!item.code) return true;
          if (seenCodes.has(item.code)) return false;
          seenCodes.add(item.code);
          return true;
        }) || [];

      runInAction(() => {
        if (framework !== "All") {
          this.disclosures.set(framework, disclosuresList);
        } else {
          // Group by framework
          this.disclosures.clear();
          disclosuresList.forEach((item) => {
            const fw = item.framework;
            if (!fw) return;
            if (!this.disclosures.has(fw)) {
              this.disclosures.set(fw, []);
            }
            this.disclosures.get(fw).push(item);
          });
        }
      });

      return disclosuresList.length;
    } catch (error) {
      showToast.error(error?.message || t("Failed to load disclosures."));
      return 0;
    } finally {
      runInAction(() => {
        this.loadingDisclosures = false;
      });
    }
  };

  /**
   * Get disclosures for a specific framework
   */
  getDisclosuresForFramework = (framework) => {
    return this.disclosures.get(framework) || [];
  };

  /**
   * Get quantitative disclosures for a framework
   */
  getQuantitativeDisclosures = (framework = "All") => {
    if (framework === "All") {
      return this.getAllQuantitativeDisclosures();
    }
    return this.getDisclosuresForFramework(framework)
      .filter((d) => d.isQuantitative)
      .map(normalizeDisclosure);
  };

  /**
   * Get all quantitative disclosures across all frameworks
   */
  getAllQuantitativeDisclosures = () => {
    const all = [];
    this.disclosures.forEach((list) => {
      list
        .filter((d) => d.isQuantitative)
        .forEach((item) => {
          all.push(normalizeDisclosure(item));
        });
    });
    return all;
  };

  /**
   * Get quantitative disclosures filtered by framework and category
   */
  getQuantitativeDisclosuresByCategory = (framework, category) => {
    const disclosures =
      framework === "All"
        ? this.getAllQuantitativeDisclosures()
        : this.getQuantitativeDisclosures(framework);

    if (category === "All") return disclosures;
    return disclosures.filter((d) => d.category === category);
  };

  getSelectedQuantitativeDisclosuresByCategory = (framework, category) => {
    const disclosures = this.getQuantitativeDisclosuresByCategory(
      framework,
      category,
    );
    if (this.selectedMetricIds.size === 0) {
      return [];
    }
    return disclosures.filter((disclosure) =>
      this.selectedMetricIds.has(this.getMetricIdentifier(disclosure)),
    );
  };

  /**
   * Get unique categories for a framework
   */
  getCategories = (framework = "All") => {
    if (framework === "All") {
      const all = new Set();
      this.disclosures.forEach((list) => {
        list.forEach((item) => {
          if (item.category) all.add(item.category);
        });
      });
      return Array.from(all);
    }

    const categories = new Set();
    this.getDisclosuresForFramework(framework).forEach((item) => {
      if (item.category) categories.add(item.category);
    });
    return Array.from(categories);
  };

  /**
   * Get disclosure count by framework
   */
  getDisclosureCountByFramework = () => {
    const counts = {};
    this.disclosures.forEach((list, framework) => {
      counts[framework] = list.filter((d) => d.isQuantitative).length;
    });
    counts.All = this.getAllQuantitativeDisclosures().length;
    return counts;
  };

  /**
   * Load targets from API
   */
  loadTargetsFromApi = async (
    targetId,
    year,
    framework = "All",
    category = "All",
  ) => {
    runInAction(() => {
      this.loadingTargets = true;
    });

    try {
      const resolvedCategory = this.isEnvOnly ? "Environmental" : category;
      const data = await esgService.getQuantitativeTargets({
        targetId,
        year,
        framework,
        category: resolvedCategory,
        page: 1,
        limit: 1000,
        throwOnError: false,
      });

      if (!data) {
        throw new Error(t("Failed to load targets."));
      }

      const targets = extractTargets(data);

      // Clear existing targets for this year
      const keysToDelete = [];
      this.targets.forEach((target, key) => {
        if (target.year === year) {
          keysToDelete.push(key);
          this.changedTargets.delete(key);
        }
      });
      keysToDelete.forEach((key) => this.targets.delete(key));

      // Add loaded targets
      this.targets = new Map();
      targets.forEach((target) => {
        const key = `${target.masterMetricId}_${year}`;
        const resolvedFramework =
          framework !== "All"
            ? framework
            : target.applicableFrameworks?.[0] || target.framework || "All";

        this.targets.set(key, {
          disclosureCode: target.masterMetricId,
          disclosureTitle: target.metricName,
          framework: resolvedFramework,
          category: target.category,
          unit: target.unit,
          annual: target.annual,
          quarterly: target.quarterly,
          monthly: target.monthly,
          year,
          notes: target.notes || "",
          createdAt: target.createdAt,
          updatedAt: target.updatedAt,
        });
      });

      return targets.length;
    } catch (error) {
      showToast.error(error?.message || t("Failed to load targets."));
      return 0;
    } finally {
      runInAction(() => {
        this.loadingTargets = false;
      });
    }
  };

  /**
   * Save changed targets to API
   */
  saveTargetsToApi = async (id) => {
    if (!this.hasChangedTargets) {
      throw new Error("No changes to save.");
    }
    if (
      !this.targetSettingListData.name ||
      this.targetSettingListData.name === ""
    ) {
      throw new Error("Enter target name!.");
    }

    runInAction(() => {
      this.savingTargets = true;
    });

    try {
      const targetYear = this.selectedYear;
      const targetId = id ? id : this.targetSettingListData?._id;
      const targetName = this.targetSettingListData.name;
      const isallOrganization = this.targetSettingListData.isallOrganization;
      const selectedMetricIds = this.selectedMetricIdsList;
      if (!selectedMetricIds.length) {
        throw new Error("Please select at least one disclosure.");
      }
      const selectedMetricSet = new Set(
        selectedMetricIds
          .map((metricId) => this.getMetricIdentifier(metricId))
          .filter(Boolean),
      );

      const yearTargets = Array.from(this.targets.values()).filter(
        (target) => target && target.year === targetYear,
      );
      const apiTargets = yearTargets
        .filter((target) =>
          selectedMetricSet.has(
            this.getMetricIdentifier(target.disclosureCode),
          ),
        )
        .map((target) => ({
          masterMetricId: target.disclosureCode,
          metricName: target.disclosureTitle,
          category: target.category,
          unit: target.unit,
          applicableFrameworks: [target.framework],
          annual: target.annual,
          quarterly: target.quarterly,
          monthly: target.monthly,
          notes: target.notes || "",
        }));

      if (apiTargets.length === 0) {
        throw new Error("Please set at least one target value before saving.");
      }

      const data = await esgService.bulkSaveQuantitativeTargets({
        year: targetYear,
        targets: apiTargets,
        selectedMetricIds,
        name: targetName,
        isallOrganization: isallOrganization,
        framework: this.selectedFramework,
        organization: isallOrganization ? [] : [this.selectedOrganization],
        ...(targetId && { targetId }),
      });

      this.changedTargets.clear();
      return extractDataPayload(data);
    } finally {
      runInAction(() => {
        this.savingTargets = false;
      });
    }
  };

  deleteTargetById = async (targetId, options = {}) => {
    const { showToast: shouldToast = true, toastMessages = {} } = options;
    runInAction(() => {
      this.deleteLoading = true;
    });
    try {
      const data = await esgService.deleteTargetSettingById(targetId);
      if (shouldToast) {
        showToast.promise(data, {
          loading: t("Deleting target setting..."),
          success: t("Target setting deleted successfully."),
          error: (err) => err.message,
          ...toastMessages,
        });
      }
      await this.getTargetList();
      return await data?.result;
    } finally {
      runInAction(() => {
        this.deleteLoading = false;
      });
    }
  };

  getTargetSettingById = async (targetId) => {
    try {
      const data = await esgService.getTargetSettingsById(targetId);
      const result = data?.result || {};
      this.targetSettingListData = result;
      this.selectedFramework = result.framework;
      this.selectedYear = result.year;
      this.selectedOrganization = Array.isArray(result.organization)
        ? result.organization[0] || null
        : null;
      const selectedMetricIds =
        Array.isArray(result.selectedMetricIds) &&
        result.selectedMetricIds.length > 0
          ? result.selectedMetricIds
          : (result.targets || []).map((target) => target.masterMetricId);
      this.setSelectedMetricIds(selectedMetricIds, { markChanged: false });
    } catch (error) {
      console.log(error);
    }
  };
  /**
   * Seed demo targets
   */
  seedTargets = async ({ year, framework, category }) => {
    runInAction(() => {
      this.seedingTargets = true;
    });

    try {
      const data = await esgService.seedQuantitativeTargets({
        year,
        framework,
        category,
      });

      // Reload targets after seeding
      await this.loadTargetsFromApi(
        this.targetSettingsList._id,
        this.selectedYear,
        this.selectedFramework,
        this.activeCategory,
      );

      return extractDataPayload(data);
    } finally {
      runInAction(() => {
        this.seedingTargets = false;
      });
    }
  };

  handleChangeQuarterly = () => {
    this.isQuarterly = !this.isQuarterly;
  };

  getTargetList = async () => {
    try {
      const data = await esgService.listTargetSettings();
      if (!data) {
        throw new Error(t("Failed to load targets."));
      }
      this.targetSettingsList = data.targets || [];
      this.filterTargetList = data.targets || [];
    } catch (error) {
      showToast.error(error?.message || t("Failed to load targets."));
      return 0;
    } finally {
      runInAction(() => {
        // this.loadingTargets = false;
      });
    }
  };
  handleSletctOrganization = (organization) => {
    this.selectedOrganization = organization;
  };

  handleChangeTargetDeails = (key, value) => {
    this.targetSettingListData[key] = value;
    this.changedTargets.add("targetSettingListData");
  };

  onManageTarget = (target) => {
    this.targetSettingListData = target;
    this.setSelectedMetricIds(target?.selectedMetricIds || [], {
      markChanged: false,
    });
  };
  onViewTargetSummary = (target) => {
    this.targetSettingListData = target;
    this.selectedFramework = target.framework;
    this.selectedYear = target.year;
    this.setSelectedMetricIds(target?.selectedMetricIds || [], {
      markChanged: false,
    });
  };
  clearTargetSettingData = () => {
    this.targetSettingListData = { isallOrganization: true };
    this.selectedFramework = null;
    this.selectedYear = new Date().getFullYear();
    this.selectedOrganization = null;
    this.targetSearchTerm = "";
    this.targets.clear();
    this.selectedMetricIds = new Set();
    this.changedTargets.clear();
  };
}

export const kpiTargetsStore = new KpiTargetsStore();
